// Gate global de trial (κ+1) — bloqueia o APP INTEIRO quando o tempo gratuito
// do dia esgota. Diferente do overlay por-player (que só interrompe o vídeo),
// este cobre tudo e é INESCAPÁVEL: a única saída é comprar (QR) ou reiniciar o
// app (ao reabrir, re-checa e volta a bloquear até comprar ou virar o dia).
//
// Decisão Fernando (2026-05-25): trial é tempo de USO do app, não de um vídeo só.
// Checa no boot (não deixa "passear" no catálogo com o trial esgotado) e fica
// fazendo polling enquanto bloqueado — quando o pagamento confirma no backend
// (liberação INSTANTÂNEA), o phase deixa de ser 'blocked' e o gate some sozinho.

import { useCallback, useEffect, useRef, useState } from 'react'

import { api } from '../api'
import { setTrialBlocked, useTrialBlocked } from '../trial-block-store'

import { TrialExhaustedOverlay } from './TrialOverlay'

// ===== Cadência (medida, não estimada) =====
// O gate está montado no provider RAIZ: o que ele faz por segundo, ele faz em
// TODO aparelho com o app aberto. A cadência ANTIGA era um `setInterval(8s)` num
// `useEffect(…, [])` — igual pra quem estava bloqueado e pra quem estava liberado
// (o `return null` esconde a UI, NÃO para o timer): 451 chamadas/h por aparelho
// (7,52 req/min). Com o teto de `/v1/trial/status` em 120 req/min por chave de
// IP (stream-session.ts STREAM_LIMIT), 16 aparelhos saturavam a rota — e esse
// "16 por casa" é o cenário OTIMISTA: vale enquanto a chave for mesmo o IP do
// cliente, o que depende da env de proxy confiável estar configurada; sem ela a
// chave é o último hop e o balde é compartilhado por muito mais gente. O 429
// resultante é PIOR que lentidão: o catch abaixo é fail-open (o gate para de
// bloquear) e o heartbeat do player é fail-closed (3 falhas = tela de compra no
// meio do vídeo). Medição e controle em `trial-gate-polling.test.tsx`.
//
// Cadência nova, pelo INVARIANTE "o que o polling muda na tela":
//  - BLOQUEADO: é o único estado em que a resposta muda algo (a liberação
//    pós-pagamento tira o overlay). Mantém os 8s originais na janela em que o
//    pagamento realmente acontece, e faz backoff depois — com RESET, pra não
//    punir quem voltou pro sofá.
//  - LIBERADO: nada na tela depende da resposta. Sobra só a defesa "não passear
//    no catálogo com o trial esgotado" pros caminhos em que o bloqueio nasce
//    FORA deste aparelho (outro device da mesma conta queima a cota do dia; a
//    janela de trial vence com a cota do dia já gasta; admin baixa o limite).
//    Esses não têm evento local pra reagir, então sobrevive uma re-checagem
//    LENTA. Os caminhos com evento local (o player deste aparelho esgotando o
//    trial) continuam INSTANTÂNEOS via trial-block-store, sem polling nenhum.
//
// NÃO é tier-aware de propósito (diferente de `epgRefreshIntervalMs`, que o vlow
// zera): entitlement não pode ter buraco que depende do aparelho — fix de device
// é GENÉRICO. O custo aqui é 1 fetch a cada 5 min, que nem o vlow sente.
const POLL_MS = 8_000
// Janela de cadência plena após (re)entrar em bloqueado: cobre scan do QR →
// escolher plano → gerar Pix → pagar no app do banco → webhook. Depois disso o
// overlay tipicamente está abandonado (ninguém na frente da TV).
const FAST_WINDOW_MS = 300_000
// Teto do backoff. 30s (e não 60s+) porque este é o surface que recebe o
// dinheiro: o pior caso de espera de quem pagou FORA da janela rápida e não
// encostou no controle é 30s. O custo do teto baixo é 2 req/min por aparelho
// BLOQUEADO (~5% do parque), irrelevante perto dos 7,5 req/min de TODO aparelho.
const BACKOFF_CEILING_MS = 30_000
// Re-checagem de quem está LIBERADO (bloqueio nascido fora deste aparelho).
const IDLE_RECHECK_MS = 300_000
// Boot que FALHOU é fail-open (o gate não bloqueia): reconverge rápido e de
// forma BOUNDED, senão um blip de rede no boot abre o app até o próximo ciclo.
const ERROR_BACKOFF_MS = [8_000, 16_000, 32_000]

export function TrialGate(): React.JSX.Element | null {
  // Bloqueio AO VIVO vindo do player (heartbeat → blocked) é imediato.
  const liveBlocked = useTrialBlocked()
  // Bloqueio confirmado pela API (boot + polling). Combina com o live: qualquer
  // um dos dois cobre o app. A API é a fonte que LIMPA (pós-pagamento/vira o dia).
  const [apiBlocked, setApiBlocked] = useState(false)
  const blocked = liveBlocked || apiBlocked
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const check = useCallback(async (): Promise<'ok' | 'error'> => {
    try {
      const s = await api.trialStatus()
      if (!mountedRef.current) return 'ok'
      const isBlocked = s.phase === 'blocked'
      setApiBlocked(isBlocked)
      // A API é autoridade: sincroniza o store live (libera pós-pagamento,
      // re-bloqueia no boot com trial esgotado). Idempotente.
      setTrialBlocked(isBlocked)
      return 'ok'
    } catch {
      // Falha de rede no status NÃO bloqueia por si só (fail-open AQUI — o
      // enforcement REAL é no start/heartbeat do player, fail-closed). Sem
      // isso, queda de rede travaria o app todo indevidamente. Mas se o player
      // já marcou blocked ao vivo, esse estado é preservado (não limpamos).
      if (mountedRef.current) setApiBlocked(false)
      return 'error'
    }
  }, [])

  // Boot: 1ª checagem assim que o app monta (não deixa "passear" no catálogo com
  // o trial esgotado). Se falhar, re-tenta em backoff BOUNDED — o fail-open do
  // catch não pode virar "gate desligado até reabrir o app".
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null
    let stopped = false
    let attempt = 0

    async function boot(): Promise<void> {
      const result = await check()
      if (stopped || result === 'ok') return
      const delay = ERROR_BACKOFF_MS[attempt]
      if (delay === undefined) return // esgotou o retry; a cadência normal segue
      attempt += 1
      timer = setTimeout(() => void boot(), delay)
    }

    void boot()
    return () => {
      stopped = true
      if (timer) clearTimeout(timer)
    }
  }, [check])

  // Cadência periódica. Reage ao ESTADO (blocked), não a um intervalo fixo.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null
    let stopped = false
    // Quando o backoff pode começar a crescer, e de quanto ele está agora.
    let fastUntil = Date.now() + FAST_WINDOW_MS
    let backoff = POLL_MS

    function nextDelay(): number {
      if (!blocked) return IDLE_RECHECK_MS
      if (Date.now() < fastUntil) return POLL_MS
      const delay = backoff
      backoff = Math.min(backoff * 2, BACKOFF_CEILING_MS)
      return delay
    }

    // Versão do ciclo. O `poke` abaixo pode chegar com um `check()` EM VOO: aí o
    // timer JÁ disparou, o `clearTimeout` é no-op e o `.then` daquele check
    // agendaria um SEGUNDO ciclo — dois se auto-perpetuando, com o cleanup
    // conhecendo só o último (cadência DOBRADA pelo resto da sessão: 12 req/min
    // no aparelho bloqueado, PIOR que os 7,52 do baseline que esta tela veio
    // consertar). Cada ciclo só se re-agenda enquanto for o ciclo corrente.
    let cycle = 0
    // Quando saiu a última requisição. É o relógio do throttle abaixo.
    let lastCheckAt = Date.now()

    function schedule(delayMs?: number): void {
      if (stopped) return
      const myCycle = cycle
      timer = setTimeout(
        () => {
          lastCheckAt = Date.now()
          void check().then(() => {
            if (myCycle !== cycle) return // ciclo aposentado por um poke
            schedule()
          })
        },
        typeof delayMs === 'number' ? delayMs : nextDelay(),
      )
    }

    // Sinal de que tem gente na frente da TV esperando a liberação (tecla do
    // controle) ou de que o app voltou do standby. LEADING-EDGE: em vez de só
    // re-armar, checa AGORA — quem apertou o controle depois de pagar recebe a
    // liberação em 1 RTT, não no próximo tick.
    //
    // ⚠️ O throttle de 1 requisição por POLL_MS é o que segura o AUTO-REPEAT do
    // controle (~10 keydown/s com a tecla pressionada): a 2ª em diante cai no
    // ramo de baixo, que só encurta o próximo tick pro ritmo pleno. Sem pedir
    // request extra e sem empurrar o poll pra frente (que seria fome).
    function poke(): void {
      if (stopped || !blocked) return
      fastUntil = Date.now() + FAST_WINDOW_MS
      backoff = POLL_MS
      const since = Date.now() - lastCheckAt
      // Aposenta o ciclo corrente — inclusive um cujo check() esteja EM VOO,
      // cujo timer já disparou e cujo clearTimeout, portanto, é no-op.
      cycle += 1
      if (timer) clearTimeout(timer)
      if (since >= POLL_MS) {
        lastCheckAt = Date.now()
        const myCycle = cycle
        void check().then(() => {
          if (myCycle !== cycle) return
          schedule()
        })
      } else {
        schedule(POLL_MS - since)
      }
    }

    function onVisibility(): void {
      if (!document.hidden) poke()
    }

    if (blocked) {
      // ⚠️ CAPTURE, e no window: é o único ponto do trajeto em que a tecla ainda
      // existe. O TrialExhaustedOverlay que este gate renderiza engole seta/OK/
      // BACK em capture no window E no document com stopPropagation() — em
      // bubble este listener NUNCA rodaria justamente pras teclas que a pessoa
      // aperta na TV (medido pelo ux-tv: seta/OK/BACK não chegavam; só tecla
      // numérica/letra chegava, que ninguém usa aqui). stopPropagation não
      // atinge listener do MESMO nó e fase, então a ordem de registro não
      // importa. Booleano (não `{capture:true}`): sem ambiguidade no piso Chr38.
      window.addEventListener('keydown', poke, true)
      document.addEventListener('visibilitychange', onVisibility)
    }
    schedule()

    return () => {
      stopped = true
      if (timer) clearTimeout(timer)
      if (blocked) {
        // Mesma fase do add — remover em bubble deixaria o listener vivo.
        window.removeEventListener('keydown', poke, true)
        document.removeEventListener('visibilitychange', onVisibility)
      }
    }
  }, [blocked, check])

  if (!liveBlocked && !apiBlocked) return null
  return <TrialExhaustedOverlay />
}
